import { upperFirst } from "lodash";
import { GroupDocument, LearningLogDocument, LearningLogPublication, PersonalDocument, PersonalPublication, PlanningDocument, ProblemDocument, ProblemPublication, SupportPublication } from "./document-types";
const kDocumentKindPattern = /^[a-z][a-zA-Z0-9]*$/;
/**
 * A document `kind` is used as a Firestore path segment (the canonical-pointer slot) as well as the registry
 * key, so a kind is restricted to a camelCase identifier: a lowercase letter followed by letters/digits, with
 * no separators or special characters. This matches the built-in document type strings (e.g.
 * "learningLogPublication") and keeps the value safe as a Firestore document id.
 */
export function isValidDocumentKind(value) {
    return kDocumentKindPattern.test(value);
}
const gDocumentKindInfoMap = {};
export function registerDocumentKind(kind, info) {
    if (!isValidDocumentKind(kind)) {
        throw new Error(`Document kind "${kind}" is not a valid identifier ` +
            `(must be camelCase letters/digits, with no separators or special characters)`);
    }
    if (gDocumentKindInfoMap[kind]) {
        throw new Error(`Document kind "${kind}" is already registered`);
    }
    gDocumentKindInfoMap[kind] = Object.assign({ kind }, info);
}
/**
 * Look a kind up by name alone, without asking which configuration defined it. Creation is the only
 * caller: a document being created takes its kind from the config in hand, so the definition found is
 * necessarily the one it is made from. Anything reading an existing document must use
 * getKindDefinitionFor instead.
 */
export function getDocumentKindInfo(kind) {
    return kind ? gDocumentKindInfoMap[kind] : undefined;
}
/**
 * This document's kind definition, or undefined when no definition applicable to it is loaded.
 *
 * The single entry point for reading a kind definition off an existing document. It takes the document
 * rather than the kind because a kind name does not identify a definition on its own: a kind declared by a
 * unit config exists only while that unit is loaded, and two units may declare the same kind meaning
 * different things (see IDocumentKindInfo.unit). Matching the document's `unit` against the definition's is
 * what tells a definition that governs this document from one that merely shares its name.
 *
 * Undefined therefore means "no definition to read here", not "no such kind" — the document may well have
 * been created from a perfectly good definition belonging to a unit that is not loaded. Sort Work lists
 * documents from every unit a class has worked through, so this is ordinary rather than exceptional, and a
 * caller must answer from the document's stored fields instead. Anything a definition contributes that
 * cannot be recovered that way has to be stamped onto the document at creation.
 */
export function getKindDefinitionFor(doc) {
    const info = getDocumentKindInfo(doc.kind);
    if (!info)
        return undefined;
    // A built-in kind declares no unit: it is registered by the application, so it governs its documents
    // wherever they came from.
    if (info.unit != null && info.unit !== doc.unit)
        return undefined;
    return info;
}
/** A kind's full stamp set (its metadataFields plus the `kind` key), or `{}` if the kind is unregistered. */
export function getDocumentKindMetadataFields(kind) {
    const info = getDocumentKindInfo(kind);
    if (!info)
        return {};
    return Object.assign({ kind: info.kind }, info.metadataFields);
}
export function getDocumentOwnerType(kind) {
    var _a, _b;
    return (_b = (_a = getDocumentKindInfo(kind)) === null || _a === void 0 ? void 0 : _a.ownerType) !== null && _b !== void 0 ? _b : "user";
}
/**
 * The owner uid to stamp on a new document of the given kind.
 *
 * Throws for a kind that is not registered, rather than defaulting to the creating user the way
 * getDocumentOwnerType does. Defaulting here would hand a group's or a class's document to whoever
 * happened to create it, and because a canonical slot is addressed by its owner, would file it in that
 * user's slot instead of the shared one — both silently.
 *
 * A unit-declared kind is registered only while the unit declaring it is loaded, so this also states the
 * rule that a document may be created only for a kind the current unit defines. Reading documents from
 * other units is unaffected: nothing on the read side resolves an owner (see document-axes.ts).
 */
export function getDocumentOwner(kind, ctx) {
    const info = getDocumentKindInfo(kind);
    if (!info) {
        throw new Error(`Cannot resolve the owner of unregistered document kind "${kind}"`);
    }
    switch (info.ownerType) {
        case "group": return required(ctx.groupOwnerId, kind, "group");
        case "class": return required(ctx.classOwnerId, kind, "class");
        case "user": return ctx.userId;
        default: return ctx.userId;
    }
}
function required(ownerId, kind, ownerType) {
    if (!ownerId) {
        throw new Error(`Cannot create a ${ownerType}-owned document of kind "${kind}": ` +
            `no ${ownerType} owner id is available`);
    }
    return ownerId;
}
/**
 * The owner fields to stamp on a new document of the given kind, beyond the owner uid getDocumentOwner
 * returns. Only a group owner has one.
 */
export function getDocumentOwnerFields(kind, ctx) {
    if (getDocumentOwnerType(kind) !== "group" || !ctx.groupId)
        return {};
    return { groupId: ctx.groupId };
}
/**
 * The container and curriculum fields to stamp on a document of the given kind, selected by its registered
 * `containerType` — which fixes both, so one lookup answers for both axes.
 */
export function getDocumentLocationFields(kind, ctx) {
    var _a;
    switch ((_a = getDocumentKindInfo(kind)) === null || _a === void 0 ? void 0 : _a.containerType) {
        case "classUnit": return {
            unit: ctx.unit,
            context_id: ctx.context_id,
            // Stated explicitly rather than omitted. A curriculum field written as null means "not about an
            // investigation or problem" (firestore.rules `hasPresentField`), the same convention class-contained
            // documents use for `unit: null`. It is what lets Sort Work query for documents about a unit but not
            // a problem — Firestore cannot match a field that is missing.
            investigation: null,
            problem: null
        };
        case "class": return {
            unit: null,
            context_id: ctx.context_id
        };
        case "offering": return {
            unit: ctx.unit,
            investigation: ctx.investigation,
            problem: ctx.problem,
            context_id: ctx.context_id,
            offeringId: ctx.offeringId
        };
        default: return {
            unit: null,
            context_id: ctx.context_id
        };
    }
}
/**
 * The display title a document's kind supplies, for the kinds whose name is a property of the kind itself
 * rather than of the document. A class-wide document is not one of them: its name is authored per slot and
 * stored on the document at creation, so it is read from the document like any other stored title.
 */
export function getDocumentTitle(document) {
    // Keyed on `type` plus `groupId`, not `kind`: a group document may have no stored `kind` yet (we backfill
    // the kind on open but need the title for the lists of documents before they are opened), so it cannot rely
    // on the lookup above. Requiring `groupId` (not just `type === GroupDocument`) matters because a class-wide
    // document also stores `type: "group"` but carries no `groupId`, so without the `groupId` check it would
    // render as "Group undefined Document" instead of falling through to `undefined`, which callers already
    // handle by reading its stored title.
    //
    // TRANSITIONAL: this reads `type` only because a group document may carry no `kind`. Once
    // scripts/backfill-group-document-axes.ts has stamped `kind` on every group document in every
    // environment, this becomes `document.kind === GroupDocument` and the `groupId` check goes away with it —
    // a class-wide document has its own kind, so it can no longer reach this branch at all.
    if (document.type === GroupDocument && document.groupId)
        return `Group ${document.groupId} Document`;
    return undefined;
}
/**
 * A readable label derived from the kind string alone: "drivingQuestionBoard" → "Driving Question
 * Board". Registry-free by design, so it can name a document no other source names — one created before
 * its title was stored, or one whose kind stores no title at all.
 *
 * It recovers the kind's identity, not the author's wording: a slot titled "Our Big Questions" reads as
 * "Driving Question Board" here.
 */
export function getDocumentKindLabel(kind) {
    if (!kind)
        return undefined;
    return upperFirst(kind.replace(/([A-Z])/g, " $1"));
}
/**
 * Register a kind declared by a unit's `classWideDocuments` configuration. Every class-wide collaborative
 * document has the same shape — concurrent, owned by the synthetic class owner, kept in the class's copy of
 * the unit and about that unit and nothing narrower — so only the kind key and the declaring unit come from
 * the configuration. The authored title is not registered: it is stamped onto each document at creation, so
 * it travels with the document instead of being resolved from a definition that may not be loaded. Throws
 * like registerDocumentKind when the kind is malformed or already registered.
 */
export function registerClassWideDocumentKind(kind, unit) {
    registerDocumentKind(kind, {
        metadataFields: { concurrent: true },
        ownerType: "class",
        containerType: "classUnit",
        unit
    });
}
function registerBuiltInDocumentKinds() {
    // A group document is kept in the offering, like the problem documents beside it; what makes it a group's
    // is its owner, which is also where its stored `groupId` comes from (see getDocumentOwnerFields).
    registerDocumentKind(GroupDocument, {
        metadataFields: { concurrent: true },
        ownerType: "group",
        containerType: "offering"
    });
    const personalLikeKindInfo = {
        metadataFields: {},
        ownerType: "user",
        containerType: "class"
    };
    registerDocumentKind(PersonalDocument, personalLikeKindInfo);
    registerDocumentKind(LearningLogDocument, personalLikeKindInfo);
    registerDocumentKind(PersonalPublication, personalLikeKindInfo);
    registerDocumentKind(LearningLogPublication, personalLikeKindInfo);
    const problemLikeKindInfo = {
        metadataFields: {},
        ownerType: "user",
        containerType: "offering"
    };
    registerDocumentKind(PlanningDocument, problemLikeKindInfo);
    registerDocumentKind(ProblemDocument, problemLikeKindInfo);
    registerDocumentKind(ProblemPublication, problemLikeKindInfo);
    registerDocumentKind(SupportPublication, problemLikeKindInfo);
}
registerBuiltInDocumentKinds();
// Test-only: reset the registry to just the built-in kinds. The registry is module-global mutable state and
// registerDocumentKind throws on duplicates, so a test that registers a kind must reset before another test
// registers the same kind. (Jest gives each test file its own module registry, so this is only needed within a
// file that registers the same kind in more than one test.)
export function resetDocumentKindRegistryForTests() {
    for (const kind of Object.keys(gDocumentKindInfoMap)) {
        delete gDocumentKindInfoMap[kind];
    }
    registerBuiltInDocumentKinds();
}
//# sourceMappingURL=data:application/json;base64,eyJ2ZXJzaW9uIjozLCJmaWxlIjoiZG9jdW1lbnQta2luZHMuanMiLCJzb3VyY2VSb290IjoiIiwic291cmNlcyI6WyIuLi8uLi8uLi8uLi9zcmMvbW9kZWxzL2RvY3VtZW50L2RvY3VtZW50LWtpbmRzLnRzIl0sIm5hbWVzIjpbXSwibWFwcGluZ3MiOiJBQUFBLE9BQU8sRUFBRSxVQUFVLEVBQUUsTUFBTSxRQUFRLENBQUM7QUFFcEMsT0FBTyxFQUNMLGFBQWEsRUFBRSxtQkFBbUIsRUFBRSxzQkFBc0IsRUFDMUQsZ0JBQWdCLEVBQUUsbUJBQW1CLEVBQUUsZ0JBQWdCLEVBQ3ZELGVBQWUsRUFBRSxrQkFBa0IsRUFBRSxrQkFBa0IsRUFDeEQsTUFBTSxrQkFBa0IsQ0FBQztBQTBEMUIsTUFBTSxvQkFBb0IsR0FBRyxxQkFBcUIsQ0FBQztBQUNuRDs7Ozs7R0FLRztBQUNILE1BQU0sVUFBVSxtQkFBbUIsQ0FBQyxLQUFhO0lBQy9DLE9BQU8sb0JBQW9CLENBQUMsSUFBSSxDQUFDLEtBQUssQ0FBQyxDQUFDO0FBQzFDLENBQUM7QUFFRCxNQUFNLG9CQUFvQixHQUFzQyxFQUFFLENBQUM7QUFFbkUsTUFBTSxVQUFVLG9CQUFvQixDQUFDLElBQVksRUFBRSxJQUFxQztJQUN0RixJQUFJLENBQUMsbUJBQW1CLENBQUMsSUFBSSxDQUFDLEVBQUUsQ0FBQztRQUMvQixNQUFNLElBQUksS0FBSyxDQUFDLGtCQUFrQixJQUFJLDhCQUE4QjtZQUNsRSw4RUFBOEUsQ0FBQyxDQUFDO0lBQ3BGLENBQUM7SUFDRCxJQUFJLG9CQUFvQixDQUFDLElBQUksQ0FBQyxFQUFFLENBQUM7UUFDL0IsTUFBTSxJQUFJLEtBQUssQ0FBQyxrQkFBa0IsSUFBSSx5QkFBeUIsQ0FBQyxDQUFDO0lBQ25FLENBQUM7SUFDRCxvQkFBb0IsQ0FBQyxJQUFJLENBQUMsbUJBQUssSUFBSSxJQUFLLElBQUksQ0FBRSxDQUFDO0FBQ2pELENBQUM7QUFFRDs7Ozs7R0FLRztBQUNILE1BQU0sVUFBVSxtQkFBbUIsQ0FBQyxJQUFrQjtJQUNwRCxPQUFPLElBQUksQ0FBQyxDQUFDLENBQUMsb0JBQW9CLENBQUMsSUFBSSxDQUFDLENBQUMsQ0FBQyxDQUFDLFNBQVMsQ0FBQztBQUN2RCxDQUFDO0FBV0Q7Ozs7Ozs7Ozs7Ozs7O0dBY0c7QUFDSCxNQUFNLFVBQVUsb0JBQW9CLENBQUMsR0FBOEI7SUFDakUsTUFBTSxJQUFJLEdBQUcsbUJBQW1CLENBQUMsR0FBRyxDQUFDLElBQUksQ0FBQyxDQUFDO0lBQzNDLElBQUksQ0FBQyxJQUFJO1FBQUUsT0FBTyxTQUFTLENBQUM7SUFDNUIscUdBQXFHO0lBQ3JHLDJCQUEyQjtJQUMzQixJQUFJLElBQUksQ0FBQyxJQUFJLElBQUksSUFBSSxJQUFJLElBQUksQ0FBQyxJQUFJLEtBQUssR0FBRyxDQUFDLElBQUk7UUFBRSxPQUFPLFNBQVMsQ0FBQztJQUNsRSxPQUFPLElBQUksQ0FBQztBQUNkLENBQUM7QUFFRCw2R0FBNkc7QUFDN0csTUFBTSxVQUFVLDZCQUE2QixDQUFDLElBQWtCO0lBQzlELE1BQU0sSUFBSSxHQUFHLG1CQUFtQixDQUFDLElBQUksQ0FBQyxDQUFDO0lBQ3ZDLElBQUksQ0FBQyxJQUFJO1FBQUUsT0FBTyxFQUFFLENBQUM7SUFDckIsdUJBQVMsSUFBSSxFQUFFLElBQUksQ0FBQyxJQUFJLElBQUssSUFBSSxDQUFDLGNBQWMsRUFBRztBQUNyRCxDQUFDO0FBRUQsTUFBTSxVQUFVLG9CQUFvQixDQUFDLElBQWtCOztJQUNyRCxPQUFPLE1BQUEsTUFBQSxtQkFBbUIsQ0FBQyxJQUFJLENBQUMsMENBQUUsU0FBUyxtQ0FBSSxNQUFNLENBQUM7QUFDeEQsQ0FBQztBQUVEOzs7Ozs7Ozs7OztHQVdHO0FBQ0gsTUFBTSxVQUFVLGdCQUFnQixDQUFDLElBQTJCLEVBQUUsR0FBMEI7SUFDdEYsTUFBTSxJQUFJLEdBQUcsbUJBQW1CLENBQUMsSUFBSSxDQUFDLENBQUM7SUFDdkMsSUFBSSxDQUFDLElBQUksRUFBRSxDQUFDO1FBQ1YsTUFBTSxJQUFJLEtBQUssQ0FBQywyREFBMkQsSUFBSSxHQUFHLENBQUMsQ0FBQztJQUN0RixDQUFDO0lBQ0QsUUFBUSxJQUFJLENBQUMsU0FBUyxFQUFFLENBQUM7UUFDdkIsS0FBSyxPQUFPLENBQUMsQ0FBQyxPQUFPLFFBQVEsQ0FBQyxHQUFHLENBQUMsWUFBWSxFQUFFLElBQUksRUFBRSxPQUFPLENBQUMsQ0FBQztRQUMvRCxLQUFLLE9BQU8sQ0FBQyxDQUFDLE9BQU8sUUFBUSxDQUFDLEdBQUcsQ0FBQyxZQUFZLEVBQUUsSUFBSSxFQUFFLE9BQU8sQ0FBQyxDQUFDO1FBQy9ELEtBQUssTUFBTSxDQUFDLENBQUUsT0FBTyxHQUFHLENBQUMsTUFBTSxDQUFDO1FBQ2hDLE9BQU8sQ0FBQyxDQUFNLE9BQU8sR0FBRyxDQUFDLE1BQU0sQ0FBQztJQUNsQyxDQUFDO0FBQ0gsQ0FBQztBQUVELFNBQVMsUUFBUSxDQUFDLE9BQTJCLEVBQUUsSUFBMkIsRUFBRSxTQUFpQjtJQUMzRixJQUFJLENBQUMsT0FBTyxFQUFFLENBQUM7UUFDYixNQUFNLElBQUksS0FBSyxDQUFDLG1CQUFtQixTQUFTLDRCQUE0QixJQUFJLEtBQUs7WUFDL0UsTUFBTSxTQUFTLHdCQUF3QixDQUFDLENBQUM7SUFDN0MsQ0FBQztJQUNELE9BQU8sT0FBTyxDQUFDO0FBQ2pCLENBQUM7QUFXRDs7O0dBR0c7QUFDSCxNQUFNLFVBQVUsc0JBQXNCLENBQ3BDLElBQTJCLEVBQUUsR0FBeUI7SUFFdEQsSUFBSSxvQkFBb0IsQ0FBQyxJQUFJLENBQUMsS0FBSyxPQUFPLElBQUksQ0FBQyxHQUFHLENBQUMsT0FBTztRQUFFLE9BQU8sRUFBRSxDQUFDO0lBQ3RFLE9BQU8sRUFBRSxPQUFPLEVBQUUsR0FBRyxDQUFDLE9BQU8sRUFBRSxDQUFDO0FBQ2xDLENBQUM7QUFlRDs7O0dBR0c7QUFDSCxNQUFNLFVBQVUseUJBQXlCLENBQ3ZDLElBQTJCLEVBQUUsR0FBNkI7O0lBRTFELFFBQVEsTUFBQSxtQkFBbUIsQ0FBQyxJQUFJLENBQUMsMENBQUUsYUFBYSxFQUFFLENBQUM7UUFDakQsS0FBSyxXQUFXLENBQUMsQ0FBQyxPQUFPO1lBQ3ZCLElBQUksRUFBRSxHQUFHLENBQUMsSUFBSTtZQUNkLFVBQVUsRUFBRSxHQUFHLENBQUMsVUFBVTtZQUMxQixnR0FBZ0c7WUFDaEcscUdBQXFHO1lBQ3JHLHFHQUFxRztZQUNyRyw4REFBOEQ7WUFDOUQsYUFBYSxFQUFFLElBQUk7WUFDbkIsT0FBTyxFQUFFLElBQUk7U0FDZCxDQUFDO1FBQ0YsS0FBSyxPQUFPLENBQUMsQ0FBQyxPQUFPO1lBQ25CLElBQUksRUFBRSxJQUFJO1lBQ1YsVUFBVSxFQUFFLEdBQUcsQ0FBQyxVQUFVO1NBQzNCLENBQUM7UUFDRixLQUFLLFVBQVUsQ0FBQyxDQUFDLE9BQU87WUFDdEIsSUFBSSxFQUFFLEdBQUcsQ0FBQyxJQUFJO1lBQ2QsYUFBYSxFQUFFLEdBQUcsQ0FBQyxhQUFhO1lBQ2hDLE9BQU8sRUFBRSxHQUFHLENBQUMsT0FBTztZQUNwQixVQUFVLEVBQUUsR0FBRyxDQUFDLFVBQVU7WUFDMUIsVUFBVSxFQUFFLEdBQUcsQ0FBQyxVQUFVO1NBQzNCLENBQUM7UUFDRixPQUFPLENBQUMsQ0FBQyxPQUFPO1lBQ2QsSUFBSSxFQUFFLElBQUk7WUFDVixVQUFVLEVBQUUsR0FBRyxDQUFDLFVBQVU7U0FDM0IsQ0FBQztJQUNKLENBQUM7QUFDSCxDQUFDO0FBWUQ7Ozs7R0FJRztBQUNILE1BQU0sVUFBVSxnQkFBZ0IsQ0FBQyxRQUE4QjtJQUM3RCwwR0FBMEc7SUFDMUcsNEdBQTRHO0lBQzVHLDRHQUE0RztJQUM1Ryx5R0FBeUc7SUFDekcsd0dBQXdHO0lBQ3hHLHNDQUFzQztJQUN0QyxFQUFFO0lBQ0YsMEZBQTBGO0lBQzFGLDhGQUE4RjtJQUM5RiwwR0FBMEc7SUFDMUcsd0ZBQXdGO0lBQ3hGLElBQUksUUFBUSxDQUFDLElBQUksS0FBSyxhQUFhLElBQUksUUFBUSxDQUFDLE9BQU87UUFBRSxPQUFPLFNBQVMsUUFBUSxDQUFDLE9BQU8sV0FBVyxDQUFDO0lBQ3JHLE9BQU8sU0FBUyxDQUFDO0FBQ25CLENBQUM7QUFFRDs7Ozs7OztHQU9HO0FBQ0gsTUFBTSxVQUFVLG9CQUFvQixDQUFDLElBQW9CO0lBQ3ZELElBQUksQ0FBQyxJQUFJO1FBQUUsT0FBTyxTQUFTLENBQUM7SUFDNUIsT0FBTyxVQUFVLENBQUMsSUFBSSxDQUFDLE9BQU8sQ0FBQyxVQUFVLEVBQUUsS0FBSyxDQUFDLENBQUMsQ0FBQztBQUNyRCxDQUFDO0FBRUQ7Ozs7Ozs7R0FPRztBQUNILE1BQU0sVUFBVSw2QkFBNkIsQ0FBQyxJQUFZLEVBQUUsSUFBWTtJQUN0RSxvQkFBb0IsQ0FBQyxJQUFJLEVBQUU7UUFDekIsY0FBYyxFQUFFLEVBQUUsVUFBVSxFQUFFLElBQUksRUFBRTtRQUNwQyxTQUFTLEVBQUUsT0FBTztRQUNsQixhQUFhLEVBQUUsV0FBVztRQUMxQixJQUFJO0tBQ0wsQ0FBQyxDQUFDO0FBQ0wsQ0FBQztBQUVELFNBQVMsNEJBQTRCO0lBQ25DLDBHQUEwRztJQUMxRyxrR0FBa0c7SUFDbEcsb0JBQW9CLENBQUMsYUFBYSxFQUFFO1FBQ2xDLGNBQWMsRUFBRSxFQUFFLFVBQVUsRUFBRSxJQUFJLEVBQUU7UUFDcEMsU0FBUyxFQUFFLE9BQU87UUFDbEIsYUFBYSxFQUFFLFVBQVU7S0FDMUIsQ0FBQyxDQUFDO0lBRUgsTUFBTSxvQkFBb0IsR0FBRztRQUMzQixjQUFjLEVBQUUsRUFBRztRQUNuQixTQUFTLEVBQUUsTUFBTTtRQUNqQixhQUFhLEVBQUUsT0FBTztLQUNkLENBQUM7SUFDWCxvQkFBb0IsQ0FBQyxnQkFBZ0IsRUFBRSxvQkFBb0IsQ0FBQyxDQUFDO0lBQzdELG9CQUFvQixDQUFDLG1CQUFtQixFQUFFLG9CQUFvQixDQUFDLENBQUM7SUFDaEUsb0JBQW9CLENBQUMsbUJBQW1CLEVBQUUsb0JBQW9CLENBQUMsQ0FBQztJQUNoRSxvQkFBb0IsQ0FBQyxzQkFBc0IsRUFBRSxvQkFBb0IsQ0FBQyxDQUFDO0lBRW5FLE1BQU0sbUJBQW1CLEdBQUc7UUFDMUIsY0FBYyxFQUFFLEVBQUc7UUFDbkIsU0FBUyxFQUFFLE1BQU07UUFDakIsYUFBYSxFQUFFLFVBQVU7S0FDakIsQ0FBQztJQUNYLG9CQUFvQixDQUFDLGdCQUFnQixFQUFFLG1CQUFtQixDQUFDLENBQUM7SUFDNUQsb0JBQW9CLENBQUMsZUFBZSxFQUFFLG1CQUFtQixDQUFDLENBQUM7SUFDM0Qsb0JBQW9CLENBQUMsa0JBQWtCLEVBQUUsbUJBQW1CLENBQUMsQ0FBQztJQUM5RCxvQkFBb0IsQ0FBQyxrQkFBa0IsRUFBRSxtQkFBbUIsQ0FBQyxDQUFDO0FBQ2hFLENBQUM7QUFDRCw0QkFBNEIsRUFBRSxDQUFDO0FBRS9CLDRHQUE0RztBQUM1Ryw0R0FBNEc7QUFDNUcsK0dBQStHO0FBQy9HLDREQUE0RDtBQUM1RCxNQUFNLFVBQVUsaUNBQWlDO0lBQy9DLEtBQUssTUFBTSxJQUFJLElBQUksTUFBTSxDQUFDLElBQUksQ0FBQyxvQkFBb0IsQ0FBQyxFQUFFLENBQUM7UUFDckQsT0FBTyxvQkFBb0IsQ0FBQyxJQUFJLENBQUMsQ0FBQztJQUNwQyxDQUFDO0lBQ0QsNEJBQTRCLEVBQUUsQ0FBQztBQUNqQyxDQUFDIn0=